<template>
    <div class="ec2-dashboard" v-loading="loading" element-loading-text="拼命加载中">
        <el-scrollbar height="calc(100vh - 120px)">
            <!-- Filter Section -->
            <div class="filter-section">
                <el-form :inline="true" :model="formInline" label-position="left" class="filter-form">
                    <el-form-item label="日期">
                        <el-date-picker 
                            v-model="formInline.month"
                            type="month"
                            format="YYYY-MM"
                            value-format="YYYYMM"
                            @change="search"
                            placeholder="选择月份"
                        />
                    </el-form-item>
                    <el-form-item label="地区">
                        <el-select v-model="formInline.region" clearable @change="search" placeholder="请选择">
                            <el-option
                                v-for="(item,index) in regionList"
                                :key="index"
                                :label="item.label"
                                :value="item.value"
                            />
                        </el-select>
                    </el-form-item>
                    <el-form-item label="Payers" v-if="curRole!='customer'">
                        <el-select v-model="formInline.payer_id" clearable filterable @change="search" placeholder="请选择">
                            <el-option
                                v-for="(item,index) in payersList"
                                :key="index"
                                :label="item.name+'('+item.payer_id+')'"
                                :value="item.payer_id"
                            />
                        </el-select>
                    </el-form-item>
                    <el-form-item label="销售" v-if="curRole=='admin'">
                        <el-select clearable filterable v-model.trim="formInline.sales" @change="search" placeholder="请选择">
                            <el-option
                                v-for="item in salesList"
                                :key="item.id"
                                :label="item.name"
                                :value="item.name"
                            />
                        </el-select>
                    </el-form-item>
                    <el-form-item label="伙伴" v-if="curRole=='admin'||curRole=='sales'">
                        <el-select clearable filterable v-model.trim="formInline.agent_id" @change="search" placeholder="请选择">
                            <el-option
                                v-for="item in agentsList"
                                :key="item.agent_id"
                                :label="item.company"
                                :value="item.agent_id"
                            />
                        </el-select>
                    </el-form-item>
                    <el-form-item label="客户" v-if="curRole!='customer'">
                        <el-select v-model="formInline.company_id" clearable filterable @change="search" placeholder="请选择">
                            <el-option
                                v-for="(item,index) in customersList"
                                :key="index"
                                :label="item.company"
                                :value="item.id"
                            />
                        </el-select>
                    </el-form-item>
                </el-form>
            </div>

            <!-- Dashboard Content -->
            <div class="dashboard-content">
                <el-row :gutter="24">
                    <!-- Cost Summary Card -->
                    <el-col :span="7" v-if="ec2Data">
                        <el-card class="cost-card" :class="{'full-height': curRole=='customer'}">
                            <div class="card-header">
                                <h3>EC2费用</h3>
                            </div>
                            <div class="cost-comparison">
                                <div class="cost-item">
                                    <div class="month">{{ ec2Data.ec2list[ec2Data.ec2list.length - 2].month }}</div>
                                    <div class="amount">{{formInline.region=='cn'?'¥':'$'}}{{ Number(ec2Data.ec2list[ec2Data.ec2list.length - 2].total).toFixed(2) }}</div>
                                    <div class="usage">{{formInline.region=='cn'?'¥':'$'}}{{ Number(ec2Data.ec2list[ec2Data.ec2list.length - 2].usage).toFixed(2) }}</div>
                                </div>
                                <div class="cost-item current">
                                    <div class="month">{{ ec2Data.EC2.month }}</div>
                                    <div class="amount">{{formInline.region=='cn'?'¥':'$'}}{{ Number(ec2Data.EC2.total).toFixed(2) }}</div>
                                    <div class="usage">{{formInline.region=='cn'?'¥':'$'}}{{ Number(ec2Data.EC2.usage).toFixed(2) }}</div>
                                </div>
                            </div>
                            <div class="trend-indicator" v-if="Number(ec2Data.ec2list[ec2Data.ec2list.length - 2].total)!=Number(ec2Data.EC2.total)">
                                {{ percentage(ec2Data.ec2list[ec2Data.ec2list.length - 2].total,ec2Data.EC2.total) }}
                                <el-icon :class="Number(ec2Data.ec2list[ec2Data.ec2list.length - 2].total)>Number(ec2Data.EC2.total) ? 'down' : 'up'">
                                    <ArrowDown v-if="Number(ec2Data.ec2list[ec2Data.ec2list.length - 2].total)>Number(ec2Data.EC2.total)"/>
                                    <ArrowUp v-else/>
                                </el-icon>
                            </div>
                        </el-card>

                        <!-- Cost Breakdown Cards -->
                        <el-row :gutter="12" v-if="curRole!='customer'" class="breakdown-row">
                            <el-col :span="12" v-for="(item, index) in [
                                {label: '积分', value: ec2Data.EC2.credit},
                                {label: 'SP抵扣', value: ec2Data.EC2.sp},
                                {label: 'RI抵扣', value: ec2Data.EC2.rifee},
                                {label: '税费', value: ec2Data.EC2.tax}
                            ]" :key="index">
                                <el-card class="metric-card">
                                    <h4>{{ item.label }}</h4>
                                    <div class="value">{{formInline.region=='cn'?'￥':'$'}}{{ Number(item.value).toFixed(2) }}</div>
                                </el-card>
                            </el-col>
                        </el-row>
                    </el-col>

                    <!-- Instance Metrics -->
                    <el-col :span="7" v-if="ec2Data">
                        <el-row :gutter="12">
                            <el-col :span="12" v-for="(item, index) in [
                                {label: '实例数量', value: ec2Data.EC2.instanceCount},
                                {label: 'EBS数量', value: ec2Data.EC2.ebsCount},
                                {label: '区域数量', value: ec2Data.EC2.regionCount},
                                {label: '快照数量', value: ec2Data.EC2.snapshotCount}
                            ]" :key="index">
                                <el-card class="metric-card">
                                    <h4>{{ item.label }}</h4>
                                    <div class="value">{{ Number(item.value) }}</div>
                                </el-card>
                            </el-col>
                        </el-row>
                        <el-card class="distribution-card">
                            <div class="card-header">
                                <h3>资源分布</h3>
                            </div>
                            <div id="resourcePieChart" class="chart-container"></div>
                        </el-card>
                    </el-col>

                    <!-- Chart Section -->
                    <el-col :span="10">
                        <el-card class="chart-card full-height">
                            <div class="card-header">
                                <h3>EC2-Computer费用详情</h3>
                            </div>
                            <div id="chart1" class="chart-container"></div>
                        </el-card>
                    </el-col>
                </el-row>

                <!-- Tables Section -->
                <div class="tables-section">
                    <el-card class="table-card">
                        <div class="card-header">
                            <h3>EC2-Instances</h3>
                        </div>
                        <el-table stripe :data="instanceList" ref="tableref" height="300">
                            <el-table-column label="账户" prop="linkId"></el-table-column>
                            <el-table-column label="区域" prop="region"></el-table-column>
                            <el-table-column label="实例ID" prop="resId" :show-overflow-tooltip="true"></el-table-column>
                            <el-table-column label="型号" prop="type"></el-table-column>
                            <el-table-column label="系统详情">
                                <template #default="scope">
                                    {{ scope.row.os +'/'+ scope.row.cpu +'c/'+ scope.row.mem }}
                                </template>
                            </el-table-column>
                            <el-table-column label="CPU型号" prop="cpufeatures" :show-overflow-tooltip="true"></el-table-column>
                            <el-table-column label="使用时长" prop="usage" align="right">
                                <template #default="scope">{{ Number(scope.row.usage).toFixed(2) }}</template>
                            </el-table-column>
                            <el-table-column label="实例成本" prop="onDemandCost" align="right">
                                <template #default="scope">{{ Number(scope.row.onDemandCost).toFixed(2) }}</template>
                            </el-table-column>
                            <el-table-column label="付费模型" prop="pricingTerm" align="right"></el-table-column>
                            <el-table-column label="节省" align="right" v-if="curRole!='customer'">
                                <template #default="scope">
                                    {{ (Number(scope.row.onDemandCost) - Number(scope.row.unblendedCost)).toFixed(2) }}
                                </template>
                            </el-table-column>
                            <el-table-column label="积分" prop="credit" align="right" v-if="curRole!='customer'">
                                <template #default="scope">
                                    {{ scope.row.credit?Number(scope.row.credit).toFixed(2):'0' }}
                                </template>
                            </el-table-column>
                            <el-table-column label="其他" prop="tax" align="right">
                                <template #default="scope">{{ Number(scope.row.tax).toFixed(2) }}</template>
                            </el-table-column>
                            <el-table-column label="合计" prop="total" align="right">
                                <template #default="scope">{{ Number(scope.row.total).toFixed(2) }}</template>
                            </el-table-column>
                        </el-table>
                        <Pagination v-bind:child-msg="pageparm" @callFather="callFather"/>
                    </el-card>

                    <el-card class="table-card">
                        <div class="card-header">
                            <h3>EC2-Volumn</h3>
                        </div>
                        <el-table stripe :data="storageList" ref="tableref1" height="300">
                            <el-table-column label="账户" prop="linkId"></el-table-column>
                            <el-table-column label="区域" prop="region"></el-table-column>
                            <el-table-column label="ID" prop="resId" :show-overflow-tooltip="true"></el-table-column>
                            <el-table-column label="型号" prop="type"></el-table-column>
                            <el-table-column label="容量" prop="usage" align="right">
                                <template #default="scope">{{ Number(scope.row.usage).toFixed(2) }}</template>
                            </el-table-column>
                            <el-table-column label="费用" prop="unblendedCost" align="right">
                                <template #default="scope">{{ Number(scope.row.unblendedCost).toFixed(2) }}</template>
                            </el-table-column>
                            <el-table-column label="积分" prop="credit" align="right" v-if="curRole!='customer'">
                                <template #default="scope">
                                    {{ scope.row.credit?Number(scope.row.credit).toFixed(2):'0' }}
                                </template>
                            </el-table-column>
                            <el-table-column label="其他" prop="tax" align="right">
                                <template #default="scope">{{ Number(scope.row.tax).toFixed(2) }}</template>
                            </el-table-column>
                            <el-table-column label="合计" prop="total" align="right">
                                <template #default="scope">{{ Number(scope.row.total).toFixed(2) }}</template>
                            </el-table-column>
                        </el-table>
                        <Pagination v-bind:child-msg="pageparm1" @callFather="callFather1"/>
                    </el-card>

                    <el-card class="table-card">
                        <div class="card-header">
                            <h3>EC2-快照</h3>
                        </div>
                        <el-table stripe :data="snapshotList" ref="tableref2" height="300">
                            <el-table-column label="账户" prop="linkId"></el-table-column>
                            <el-table-column label="区域" prop="region"></el-table-column>
                            <el-table-column label="ID" prop="resId" :show-overflow-tooltip="true">
                                <template #default="scope">{{ scope.row.resId.split('/')[1] }}</template>
                            </el-table-column>
                            <el-table-column label="型号" prop="type"></el-table-column>
                            <el-table-column label="容量" prop="usage" align="right">
                                <template #default="scope">{{ Number(scope.row.usage).toFixed(2) }}</template>
                            </el-table-column>
                            <el-table-column label="快照费" prop="unblendedCost" align="right">
                                <template #default="scope">{{ Number(scope.row.unblendedCost).toFixed(2) }}</template>
                            </el-table-column>
                            <el-table-column label="积分" prop="credit" align="right" v-if="curRole!='customer'">
                                <template #default="scope">
                                    {{ scope.row.credit?Number(scope.row.credit).toFixed(2):'0' }}
                                </template>
                            </el-table-column>
                            <el-table-column label="其他" prop="tax" align="right">
                                <template #default="scope">{{ Number(scope.row.tax).toFixed(2) }}</template>
                            </el-table-column>
                            <el-table-column label="合计" prop="total" align="right">
                                <template #default="scope">{{ Number(scope.row.total).toFixed(2) }}</template>
                            </el-table-column>
                        </el-table>
                        <Pagination v-bind:child-msg="pageparm2" @callFather="callFather2"/>
                    </el-card>
                </div>
            </div>
        </el-scrollbar>
    </div>
</template>

<script>
    import {getEC2Data,getEC2Details,getSalesList,getAgentList,getCustomerList,getPayerList} from '@/api/api'
    import * as echarts from 'echarts';
    import Pagination from "@/components/Pagination";
    export default{
        name:'ec2Summary',
        components:{
            Pagination
        },
        data(){
            return{
                agentsList:[],
                payersList:[],
                customersList:[],
                salesList:[],
                formInline:{
                    month:0,
                    region:'cn',
                    sales:'',
                    agent_id:'',
                    payer_id:'',
                    company_id:''
                },
                regionList:[
                    {label:'全部',value:''},
                    {label:'中国',value:'cn'},
                    {label:'Global',value:'global'} 
                ],
                loading:false,
                ec2Data:null,
                instanceParams:{
                    type:'instance',
                    page:1,
                    limit:20
                },
                pageparm: {
                    page: 1,
                    limit: 20,
                    total: 0
                },
                instanceList:[],
                storageParams:{
                    type:'storage',
                    page:1,
                    limit:20
                },
                pageparm1: {
                    page: 1,
                    limit: 20,
                    total: 0
                },
                storageList:[],
                snapshotParams:{
                    type:'snapshot',
                    page:1,
                    limit:20
                },
                pageparm2: {
                    page: 1,
                    limit: 20,
                    total: 0
                },
                snapshotList:[],
                curRole:localStorage.getItem('curRole'),
            }
        },
        created(){
            let date = new Date();
            //获取当前年份的后一年
            let endY =date.getFullYear();
            //获取上一个月
            let endM = (date.getMonth() < 10 ? '0' + (date.getMonth() ) : date.getMonth());
            this.formInline.month = endY+endM;
            if(this.curRole=='agent'||this.curRole=='customer'){
                this.formInline.region='global'
            }
            this.getPermissionList();
        },
        methods:{
            async getPermissionList(){
                this.loading = true;
                if(this.curRole!='customer'){
                    await this.getPayersList();
                    await this.getAgentsList();
                }
                if(this.curRole=='admin'||this.curRole=='sales'){
                    await this.getSaleList();
                }
                await this.getCustomersList();
                await this.search()
                this.loading = false;
            },
            //获取列表
            async search() {
                await getEC2Data(this.formInline).then(res => {
                    if(res.code ==2000) {
                        this.ec2Data = res.data;
                        this.ec2Data.EC2.usage = Number(this.ec2Data.EC2.usage).toFixed(2);
                        this.ec2Data.EC2.tax = Number(this.ec2Data.EC2.tax).toFixed(2);
                        this.ec2Data.EC2.credit = Number(this.ec2Data.EC2.credit).toFixed(2);
                        this.ec2Data.EC2.rifee = Number(this.ec2Data.EC2.rifee).toFixed(2);
                        this.ec2Data.EC2.sp = Number(this.ec2Data.EC2.sp).toFixed(2);
                        this.ec2Data.EC2.others = Number(this.ec2Data.EC2.others).toFixed(2);
                        this.ec2Data.EC2.total = 
                            (Number(this.ec2Data.EC2.usage)+
                            Number(this.ec2Data.EC2.tax)+
                            Number(this.ec2Data.EC2.credit)+
                            Number(this.ec2Data.EC2.sp)+
                            Number(this.ec2Data.EC2.rifee)).toFixed(2);
                        var xData=[],yData={
                            usage:[],
                            rifee:[],
                            sp:[],
                            credit:[],
                            tax:[],
                            others:[]
                        };
                        this.ec2Data.ec2list.forEach(el=>{
                            el.usage = Number(el.usage).toFixed(2);
                            el.tax = Number(el.tax).toFixed(2);
                            el.credit = Number(el.credit).toFixed(2);
                            el.rifee = Number(el.rifee).toFixed(2);
                            el.sp = Number(el.sp).toFixed(2);
                            el.others = Number(el.others).toFixed(2);
                            el.total = (Number(el.usage)+
                                        Number(el.tax)+
                                        Number(el.credit)+
                                        Number(el.sp)+
                                        Number(el.rifee)).toFixed(2);
                            xData.push(el.month);
                            yData.usage.push(el.usage);
                            yData.rifee.push(el.rifee);
                            yData.sp.push(el.sp);
                            yData.credit.push(el.credit);
                            yData.tax.push(el.tax);
                            yData.others.push(el.others);
                        })
                        this.$nextTick(()=>{
                            this.loadLineChart(document.getElementById('chart1'),xData,yData);
                            this.loadResourcePieChart();
                        })
                    }else{
                        this.$message.error(JSON.stringify(res.msg));
                    }
                })
                await this.getDetails(1)
                await this.getDetails(2)
                await this.getDetails(3)
            },
            async getDetails(type){
                var data={};
                if(type==1){
                    data = Object.assign({},this.formInline,this.instanceParams);
                }else if(type == 2){
                    data = Object.assign({},this.formInline,this.storageParams);
                }else{
                    data = Object.assign({},this.formInline,this.snapshotParams);
                }
                await getEC2Details(data).then(res=>{
                    if(res.code==2000){
                        if(type==1){
                            this.instanceList = res.data;
                            this.pageparm.page = res.page;
                            this.pageparm.limit = res.limit;
                            this.pageparm.total = res.total;
                        }else if(type==2){
                            this.storageList = res.data;
                            this.pageparm1.page = res.page;
                            this.pageparm1.limit = res.limit;
                            this.pageparm1.total = res.total;
                        }else{
                            this.snapshotList = res.data;
                            this.pageparm2.page = res.page;
                            this.pageparm2.limit = res.limit;
                            this.pageparm2.total = res.total;
                        }
                    }else{
                        this.$message.error(JSON.stringify(res.msg));
                    }
                })
            },
            callFather(parm) {
                this.instanceParams.page = parm.page
                this.instanceParams.limit = parm.limit
                this.getDetails(1)
            },
            callFather1(parm) {
                this.storageParams.page = parm.page
                this.storageParams.limit = parm.limit
                this.getDetails(2)
            },
            callFather2(parm) {
                this.snapshotParams.page = parm.page
                this.snapshotParams.limit = parm.limit
                this.getDetails(3)
            },
            // 获取伙伴列表
            async getAgentsList(){
                await getAgentList({company:'',payers:'',sales:'',page:1,limit:999999}).then(res=>{
                    if(res.code==2000){
                        this.agentsList=res.data.data;
                    }else{
                        this.$message.error(JSON.stringify(res.msg));
                    }
                })
            }, 
            // 获取销售列表
            async getSaleList(){
                await getSalesList({name:'',phone:'',page:1,limit:999999}).then(res=>{
                    if(res.code==2000){
                        this.salesList=res.data.data;
                    }else{
                        this.$message.error(JSON.stringify(res.msg));
                    }
                })
            },
            // 获取客户列表
            async getCustomersList(){
                await getCustomerList({company:'',page:1,limit:999999}).then(res=>{
                    if(res.code==2000){
                        this.customersList=res.data.data;
                    }else{
                        this.$message.error(JSON.stringify(res.msg));
                    }
                })
            },
            // 获取payer列表
            async getPayersList(){
                await getPayerList({name:'',payer_id:'',region:'',page:1,limit:999999}).then(res=>{
                    if(res.code==2000){
                        this.payersList=res.data.data;
                    }else{
                        this.$message.error(JSON.stringify(res.msg));
                    }
                })
            }, 
            // 计算两个值的percent,num1为上月费用，num2为当月费用
            percentage(num1,num2){
                var numA = Number(num1).toFixed(2);
                var numB = Number(num2).toFixed(2);
                let upOrDown = '', percent = '';
                if (Number(numA) < Number(numB)) {
                    upOrDown = '+';
                    percent = Math.abs((((Number(numB) - Number(numA)) / Number(numA))*100).toFixed(2)) + '%';
                } else if (Number(numA) > Number(numB)) {
                    upOrDown = '-';
                    percent = Math.abs((((Number(numA) - Number(numB)) / Number(numA))*100).toFixed(2)) + '%';
                }
                return `${upOrDown}${percent}`
            },

            loadLineChart(dom,xData,yData){
                var myChart = echarts.init(dom);
                var option,_this = this;
                option = {
                    tooltip: {
                        trigger: 'axis',
                        valueFormatter:function(value){
                            if(_this.formInline.region=='cn'){
                                return '￥'+value
                            }else{
                                return '$'+value
                            }
                        }
                    },
                    legend: {
                        data: ['Usage', 'RIFee', 'SP抵扣', '积分', 'Tax','Other']
                    },
                    grid: {
                        left: '3%',
                        right: '4%',
                        bottom: '3%',
                        containLabel: true
                    },
                    xAxis: {
                        type: 'category',
                        boundaryGap: false,
                        data: xData
                    },
                    yAxis: {
                        type: 'value'
                    },
                    series: [
                        {
                            name: 'Usage',
                            type: 'line',
                            stack: 'Total',
                            data: yData.usage
                        },
                        {
                            name: 'RIFee',
                            type: 'line',
                            stack: 'Total',
                            data: yData.rifee
                        },
                        {
                            name: 'SP抵扣',
                            type: 'line',
                            stack: 'Total',
                            data: yData.sp
                        },
                        {
                            name: '积分',
                            type: 'line',
                            stack: 'Total',
                            data: yData.credit
                        },
                        {
                            name: 'Tax',
                            type: 'line',
                            stack: 'Total',
                            data: yData.tax
                        },
                        {
                            name: 'Other',
                            type: 'line',
                            stack: 'Total',
                            data: yData.others
                        }
                    ]
                };
                option && myChart.setOption(option);
            },
            
            loadResourcePieChart() {
                const pieChart = echarts.init(document.getElementById('resourcePieChart'));
                const option = {
                    tooltip: {
                        trigger: 'item',
                        formatter: '{b}: {c} ({d}%)'
                    },
                    legend: {
                        orient: 'horizontal',
                        bottom: 0,
                        data: ['实例', 'EBS', '快照']
                    },
                    series: [
                        {
                            type: 'pie',
                            radius: ['40%', '70%'],
                            avoidLabelOverlap: false,
                            itemStyle: {
                                borderRadius: 10,
                                borderColor: '#fff',
                                borderWidth: 2
                            },
                            label: {
                                show: false,
                                position: 'center'
                            },
                            emphasis: {
                                label: {
                                    show: true,
                                    fontSize: '20',
                                    fontWeight: 'bold'
                                }
                            },
                            labelLine: {
                                show: false
                            },
                            data: [
                                { value: this.ec2Data.EC2.instanceCount, name: '实例' },
                                { value: this.ec2Data.EC2.ebsCount, name: 'EBS' },
                                { value: this.ec2Data.EC2.snapshotCount, name: '快照' }
                            ]
                        }
                    ]
                };
                pieChart.setOption(option);
            },
        },
    }
</script>
<style lang="scss">
.ec2-dashboard {
    min-height: calc(100vh - 120px);
    background: #f5f7fa;
    padding: 24px;

    .filter-section {
        background: white;
        border-radius: 8px;
        padding: 16px 20px;
        margin-bottom: 24px;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);

        .filter-form {
            display: flex;
            align-items: center;
            flex-wrap: nowrap;

            .el-form-item {
                margin-bottom: 0;
                margin-right: 16px;

                &:last-child {
                    margin-right: 0;
                }

                .el-form-item__label {
                    padding-right: 8px;
                }

                .el-select, .el-date-editor {
                    width: 140px;
                }
            }

            .el-form-item:nth-child(1), 
            .el-form-item:nth-child(2) {
                .el-select, .el-date-editor {
                    width: 110px;
                }
            }
        }
    }

    .dashboard-content {
        .el-card {
            border-radius: 8px;
            box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
            margin-bottom: 24px;

            .card-header {
                margin-bottom: 20px;
                h3 {
                    font-size: 18px;
                    color: #2c3e50;
                    margin: 0;
                    padding-left: 12px;
                    border-left: 4px solid #409eff;
                }
            }
        }

        .cost-card {
            height: 200px;
            margin-bottom: 12px;
            .cost-comparison {
                display: flex;
                justify-content: space-between;
                margin-bottom: 20px;

                .cost-item {
                    text-align: center;
                    flex: 1;
                    padding: 16px;
                    border-radius: 8px;
                    background: #f8fafc;

                    &.current {
                        background: #ecf5ff;
                    }

                    .month {
                        font-size: 16px;
                        color: #606266;
                        margin-bottom: 8px;
                    }

                    .amount {
                        font-size: 24px;
                        font-weight: bold;
                        color: #303133;
                        margin-bottom: 8px;
                    }

                    .usage {
                        font-size: 14px;
                        color: #909399;
                    }
                }
            }

            .trend-indicator {
                text-align: center;
                font-size: 20px;
                color: #606266;

                .el-icon {
                    margin-left: 8px;
                    font-size: 20px;

                    &.up { color: #67c23a; }
                    &.down { color: #f56c6c; }
                }
            }
        }

        .metric-card {
            height: 120px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            text-align: center;

            h4 {
                font-size: 14px;
                color: #606266;
                margin: 0 0 12px;
            }

            .value {
                font-size: 24px;
                font-weight: bold;
                color: #303133;
            }
        }

        .chart-card {
            height: 420px;

            .chart-container {
                height: 320px;
            }
        }

        .distribution-card {
            height: 200px;
            margin-top: 12px;
            
            .chart-container {
                height: 140px;
            }
        }

        .table-card {
            .el-table {
                margin-top: 16px;
            }
        }
    }

    .tables-section {
        margin-top: 24px;
    }
}
</style>